import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Dropdown, PhoneInput } from 'components'

// Utils and Services
import useBranch from 'hooks/use-branch'
import useWorkFlowLookup from 'hooks/lookup/use-workflow-lookup'

// Types
import { LeadDetails } from 'types/global'
import { WidthProps } from 'types/styled'
import { DUP_CHECK_ERRORS } from 'modules/Leads/NewLead/constants'
import DuplicateErrorMsg from 'modules/Leads/shared/DuplicateErrorMsg'

type Props = {
  disabled?: boolean
  label?: string
  error?: string
  name: string
  showError?: boolean
  variant?: 'labeled' | 'simple'
  width?: WidthProps
  index?: number
  isLoading?: boolean
  value: LeadDetails.Mobile
  onChange: (value: LeadDetails.Mobile) => void
  onBlur?: () => void
}

const MobileNumber: React.FC<Props> = ({
  disabled = false,
  error,
  label = 'mobile_number_lbl',
  name,
  showError = true,
  variant = 'labeled',
  width = 'auto',
  isLoading = false,
  value,
  onChange,
  onBlur,
  ...props
}) => {
  const { t } = useTranslation()

  const currentBranch = useBranch()
  const { mobileContactInfoTypeOptions } = useWorkFlowLookup({})

  const contactType = useMemo(() => {
    if (!value?.contactInfoTypeId) {
      return { label: '', value: '' }
    }
    return mobileContactInfoTypeOptions.find((i) => i.value === value.contactInfoTypeId) ?? { label: '', value: '' }
  }, [value, mobileContactInfoTypeOptions])

  const isPotentialDupErr = error === DUP_CHECK_ERRORS.POTENTIAL_DUP_MOBILE
  const isDuplicateError = error === DUP_CHECK_ERRORS.EXACT_DUP_MOBILE || isPotentialDupErr

  return (
    <Container width={width} {...props}>
      {variant === 'labeled' && (
        <LabelText disabled={disabled}>
          {t(label)}
          <RequiredText>*</RequiredText>
        </LabelText>
      )}
      <InputContainer>
        <Dropdown
          name={`${name}.contactInfoTypeId`}
          inline
          isDisabled={disabled}
          mr="8px"
          onChange={(item) => {
            onChange({
              ...value,
              contactInfoTypeId: item.value,
            })
          }}
          options={mobileContactInfoTypeOptions}
          value={contactType}
          width={140}
          menuWidth={180}
          onBlur={() => {
            onBlur && onBlur()
          }}
        />
        <PhoneInput
          onChange={(e) => {
            onChange({
              ...value,
              ...e,
              value: `${e.intlFormat?.replace(/\s+0?/, '').replaceAll(' ', '')}`,
            })
          }}
          defaultCountryCode={currentBranch.countryCode}
          disabled={disabled || isLoading}
          error={error}
          errorVariant={error === DUP_CHECK_ERRORS.POTENTIAL_DUP_MOBILE ? 'warning' : 'error'}
          value={value}
          showError={showError}
          showInputLoader={isLoading}
          onBlur={() => {
            onBlur && onBlur()
          }}
          customError={
            isDuplicateError ? (
              <DuplicateErrorMsg message={error} type={isPotentialDupErr ? 'warning' : 'error'} />
            ) : undefined
          }
        />
      </InputContainer>
    </Container>
  )
}

const Container = styled.div<{ width?: WidthProps }>`
  width: ${(p) => (p.width === 'auto' ? '100%' : `${p.width}px`)};
  margin-bottom: 8px;
`

const InputContainer = styled.div`
  display: flex;
`

const LabelText = styled.div<{ disabled?: boolean }>`
  ${(p) => p.theme.font({ color: p.disabled ? 'gray4' : 'defaultText' })}
  margin-bottom: 6px;
`

const RequiredText = styled.span`
  color: ${(p) => p.theme.colors.red};
  margin-left: 4px;
`

export default MobileNumber
